import { DownloadOutlined } from '@ant-design/icons';
import { Button, Form, message } from 'antd';
import { Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetModulesListQuery } from '../../../api/queries';
import { ModuleRole } from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany, useCompanyContext } from '../../../components/contexts/CompanyContextManager';
import DownloadModal, { type DownloadState } from '../../../components/fields/DownloadModal/DownloadModal';
import { usePasswordModal } from '../../../components/fields/DownloadModal/PasswordModal';
import { exportTradeIn } from '../../../utilities/export';

const allOption = { label: 'All', value: 'all' };

const initialValues: DownloadState = {
    module: null,
    period: { start: undefined, end: undefined },
};

type DownloadProps = {
    visible: boolean;
    setVisible: (v: boolean) => void;
};

const Download = ({ visible, setVisible }: DownloadProps) => {
    const [downloading, setDownloading] = useState(false);

    const company = useCompany(true);
    const passwordModal = usePasswordModal();
    const { t } = useTranslation('tradeInList');
    const { token } = useAccountContext();
    const { companies } = useCompanyContext();

    const setDefault = useCallback(() => {
        setVisible(false);
        setDownloading(false);
    }, [setVisible, setDownloading]);

    const { data, loading } = useGetModulesListQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyIds: company ? [company.id] : companies.map(company => company.id),
                moduleRole: ModuleRole.TradeIn,
            },
        },
    });

    const modules = useMemo(() => data?.modules.items || [], [data?.modules.items]);
    const channelModuleOption = useMemo(() => {
        const options = modules.map(module => ({ label: module.displayName, value: module.id }));

        return [...(company && options.length > 1 ? [allOption] : []), ...options];
    }, [company, modules]);

    const onSubmit = useCallback(
        async (values, actions) => {
            if (!values.module) {
                return;
            }
            try {
                setDownloading(true);

                const moduleIds =
                    values.module === allOption.value
                        ? channelModuleOption
                              .filter(option => option.value !== allOption.value)
                              .map(({ value }) => value)
                        : [values.module];

                // all modules are on the same company
                const selectedModule = modules.find(data => data.id === moduleIds[0]);
                const companyName = selectedModule.company.displayName;
                const password = await exportTradeIn({
                    moduleIds,
                    period: values.period,
                    companyName,
                    token,
                });

                if (password) {
                    passwordModal.open(password);
                }
            } catch (error) {
                message.error(t('ciModal:messages.noRecordToDownload'));
            } finally {
                // Pre-select module if there is no other choice
                if (channelModuleOption.length !== 1) {
                    actions.resetForm();
                } else {
                    actions.resetForm({
                        values: {
                            module: values.module,
                            period: initialValues.period,
                        },
                    });
                }
                setDefault();
            }
        },
        [channelModuleOption, modules, passwordModal, setDefault, t, token]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ handleSubmit }) => (
                <Form id="downloadModalForm" name="downloadModalForm" onSubmitCapture={handleSubmit}>
                    <DownloadModal
                        channelModuleOption={channelModuleOption}
                        downloading={downloading}
                        loading={loading}
                        namespace="tradeInList"
                        passwordModal={passwordModal}
                        setDefault={setDefault}
                        visible={visible}
                        forAdmin
                    />
                </Form>
            )}
        </Formik>
    );
};

export const useDownload = () => {
    const { t } = useTranslation('tradeInList');
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => (
            <>
                <Button icon={<DownloadOutlined />} onClick={actions.open} type="primary">
                    {t('tradeInList:actions.download')}
                </Button>
                <Download setVisible={setVisible} visible={visible} />
            </>
        ),
    };
};

export default useDownload;
